import { useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import {
  useDraftQuestions,
  usePublishQuestion,
} from "@/hooks/use-questions";
import { getQuestionText, getChoiceText, getExplanation } from "@/lib/localize";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Check, FileText } from "lucide-react";
import { Markdown } from "@/components/ui/markdown";

export const Route = createFileRoute("/_main/exams/$slug/admin/questions/review")({
  component: ReviewDraftsPage,
});

function ReviewDraftsPage() {
  const { slug: examSlug } = Route.useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();

  const { data: draftData, isLoading: loading } = useDraftQuestions(examSlug);
  const questions = draftData?.questions ?? [];
  const publishMutation = usePublishQuestion(examSlug);
  const publishingId =
    publishMutation.isPending && typeof publishMutation.variables === "number"
      ? publishMutation.variables
      : null;

  useEffect(() => {
    if (!authLoading && user?.role !== "admin") {
      navigate({
        to: "/exams/$slug",
        params: { slug: examSlug },
        replace: true,
      });
    }
  }, [authLoading, user, navigate, examSlug]);

  async function handlePublish(id: number) {
    try {
      await publishMutation.mutateAsync(id);
    } catch {
      alert("승인에 실패했습니다.");
    }
  }

  if (authLoading || user?.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/exams/$slug/admin/questions"
              params={{ slug: examSlug }}
            >
              <Button variant="ghost" size="sm" className="cursor-pointer gap-1">
                <ArrowLeft className="h-4 w-4" />
                {t("common.back")}
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">{t("admin.review_title")}</h1>
            <Badge variant="secondary">{questions.length}{t("common.count_suffix")}</Badge>
          </div>
          <Link
            to="/exams/$slug/admin/questions/generate"
            params={{ slug: examSlug }}
          >
            <Button variant="outline" size="sm" className="cursor-pointer">
              {t("admin.review_ai_generated")}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
          </div>
        ) : questions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-zinc-400">
              <FileText className="mb-3 h-10 w-10" />
              <p className="text-sm font-medium">{t("admin.review_no_drafts")}</p>
              <p className="mt-1 text-xs">{t("admin.review_no_drafts_desc")}</p>
              <Link
                to="/exams/$slug/admin/questions/generate"
                params={{ slug: examSlug }}
                className="mt-4"
              >
                <Button size="sm" className="cursor-pointer">
                  {t("admin.review_generate")}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {questions.map((q) => (
              <Card key={q.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">
                        {t("questions.question_num")}{q.question_number}
                      </CardTitle>
                      <CardDescription>
                        {q.is_multiple ? t("common.multiple_choice") : t("common.single_choice")}
                      </CardDescription>
                    </div>
                    <Badge variant="outline">{t("common.draft")}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-lg bg-zinc-100 p-3 text-sm dark:bg-zinc-900">
                    <Markdown>{getQuestionText(q, locale)}</Markdown>
                  </div>

                  {q.choices && q.choices.length > 0 && (
                    <div className="space-y-1.5">
                      {q.choices.map((c) => (
                        <div
                          key={c.label}
                          className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                            c.is_correct
                              ? "bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200"
                              : "bg-zinc-50 dark:bg-zinc-900"
                          }`}
                        >
                          <span className="font-medium">{c.label}.</span>
                          <span>{getChoiceText(c, locale)}</span>
                          {c.is_correct && (
                            <Badge variant="default" className="ml-auto text-[10px]">
                              {t("common.correct")}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {getExplanation(q, locale) && (
                    <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 text-sm dark:border-blue-900 dark:bg-blue-950/50">
                      <p className="mb-1 text-xs font-medium text-blue-600">{t("common.explanation")}</p>
                      <Markdown>{getExplanation(q, locale)!}</Markdown>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      className="cursor-pointer"
                      disabled={publishingId === q.id}
                      onClick={() => handlePublish(q.id)}
                    >
                      {publishingId === q.id ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="mr-1 h-3 w-3" />
                      )}
                      {t("admin.review_approve")}
                    </Button>
                    <Link
                      to="/exams/$slug/admin/questions/$id/edit"
                      params={{ slug: examSlug, id: String(q.id) }}
                    >
                      <Button variant="outline" size="sm" className="cursor-pointer">
                        {t("common.edit")}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
